/**
 * Copyright 2021-2022 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { getMachines } from '../../api/machines';
import { actions } from '../actionTypes';
import MachineHandling from '../../utilities/MachineHandling';
import storage from 'redux-persist/lib/storage';
import persistReducer from 'redux-persist/es/persistReducer';
import { IMachine, anyTodo, IFaultyMachine } from '@umati/dashboard-types';
import { Action, Reducer } from 'redux';

export interface IMachinesStore {
  Machines: IMachine[];
  FaultyMachines: IFaultyMachine[];
  favourites: string[];
  apiError: anyTodo;
  onlineMachines: string[];
}

const initialState: IMachinesStore = {
  Machines: [],
  FaultyMachines: [],
  favourites: ['umati_machine'],
  apiError: null,
  onlineMachines: []
};

const machines = (state: IMachinesStore = initialState, action): IMachinesStore | undefined => {
  // omit reducer logic
  switch (action.type) {
    case actions.SET_MACHINES:
      state = {
        ...state,
        Machines: [...action.payload]
      };
      return state;
    case actions.SET_MACHINE_FAVOURITES:
      state = {
        ...state, favourites: [...new Set<string>(action.payload.favourites)]
      };
      return state;
    case actions.SET_MACHINE_FAVOURITE:
      {
        const machineId: string = action.payload.machineId;
        const favourite: boolean = action.payload.favourite;
        const favourites = new Set<string>(state.favourites);
        if (favourite) {
          favourites.add(machineId);
        } else {
          favourites.delete(machineId);
        }
        const newMachines = MachineHandling.setFavorite(state.Machines, [...favourites]);
        state = {
          ...state,
          favourites: [...favourites],
          Machines: newMachines
        };
        return state;
      }
    case actions.UPDATE_MACHINES:
      {
        let newMachines = MachineHandling.updateList(state.Machines, action.payload.newMachines);
        newMachines = MachineHandling.splitLocation(newMachines);
        newMachines = MachineHandling.setOnline(newMachines, state.onlineMachines);
        newMachines = MachineHandling.setFavorite(newMachines, state.favourites);

        state = {
          apiError: state.apiError,
          Machines: newMachines,
          favourites: state.favourites,
          FaultyMachines: state.FaultyMachines,
          onlineMachines: state.onlineMachines
        };
        return state;
      }
    case actions.UPDATE_FAULTY_MACHINES:
      {
        let newMachines = action.payload.newMachines;
        for (const m of newMachines) {
          m.ErrorType = action.payload.errorType;
        }
        newMachines = newMachines.concat(state.FaultyMachines);
        const arrayUniqueByKey = [...new Map(newMachines.map(item =>
          [item.Error, item])).values()] as IFaultyMachine[];

        state = {
          apiError: state.apiError,
          Machines: state.Machines,
          favourites: state.favourites,
          FaultyMachines: arrayUniqueByKey,
          onlineMachines: state.onlineMachines
        };
        return state;
      }
    case actions.LOAD_MACHINES_ERROR:
      state = {
        ...state,
        apiError: action.payload
      };
      return state;
    case actions.UPDATE_ONLINE_MACHINES: {
      const onlineMachines: string[] = action.payload.onlineMachines;
      const machines = MachineHandling.setOnline(state.Machines, state.onlineMachines);
      state = {
        ...state,
        Machines: machines,
        onlineMachines: onlineMachines
      };
      return state;
    }
    default:
      return state;
  }
};

const persistConfig = {
  key: 'machines',
  storage,
  blacklist: ['Machines', 'apiError', 'FaultyMachines']
};

export default persistReducer(persistConfig, machines as Reducer<IMachinesStore, Action>);

export async function fetchMachines(dispatch /* , getState */) {
  try {
    const response = await getMachines();
    const filteredMachines = MachineHandling.deserializeMachinesFromBackendRequest(response);
    const machines = MachineHandling.splitLocation(filteredMachines);
    dispatch({ type: actions.UPDATE_MACHINES, payload: { newMachines: machines } });
  } catch (error) {
    console.error(error);
    dispatch({ type: actions.LOAD_MACHINES_ERROR, payload: error });
  }
}

export const updateMachines = (newMachines, typeDefinition?: string) => {
  return (dispatch /* , getState */) => {
    dispatch({
      type: actions.UPDATE_MACHINES,
      payload: {
        newMachines,
        typeDefinition
      }
    });
  };
};

export const updateOnlineMachines = (onlineMachines: string[]) => {
  return (dispatch /* , getState */) => {
    dispatch({
      type: actions.UPDATE_ONLINE_MACHINES,
      payload: {
        onlineMachines
      }
    });
  };
};

export const updateFaultyMachines = (newMachines, errorType?: string) => {
  return (dispatch /* , getState */) => {
    dispatch({
      type: actions.UPDATE_FAULTY_MACHINES,
      payload: {
        newMachines,
        errorType
      }
    });
  };
};
